// Three key stats (Figma 6798:46464 desktop, 8106:57264 tablet, 6860:34066 mobile, 6890:41517 states).
// Clicking a card moves it to centre and applies the purple featured treatment.

"use client";

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel-v2";
import type { CarouselApi } from "@/components/ui/carousel-v2";
import { DottedGridOverlay } from "@/components/ui/dotted-grid-overlay";
import { cn } from "@/lib/utils";

type CardVisual = "revenue" | "customers" | "timeline";

export interface ThreeKeyStatCard {
  title: string;
  description: string;
}

export interface ThreeKeyStatsProps {
  left: ThreeKeyStatCard;
  center: ThreeKeyStatCard;
  right: ThreeKeyStatCard;
  defaultActiveIndex?: 0 | 1 | 2;
  className?: string;
  "data-testid"?: string;
}

interface StatCardData extends ThreeKeyStatCard {
  visual: CardVisual;
}

/** Figma 8106:57264 tablet / 6798:46464 desktop — overlapping 3-up card width. */
const OVERLAP_CARD_WIDTH = "w-[457px] xl:w-[min(457px,calc((100%+91px)/3))]";
const OVERLAP_SIDE_HEIGHT = "min-h-[497px]";
const OVERLAP_FEATURED_HEIGHT = "min-h-[549px]";

const FEATURED_SHADOW =
  "shadow-[169px_345px_108px_0px_rgba(0,0,0,0),108px_221px_98px_0px_rgba(0,0,0,0.01),61px_124px_83px_0px_rgba(0,0,0,0.02),27px_55px_62px_0px_rgba(0,0,0,0.04),7px_14px_34px_0px_rgba(0,0,0,0.05)]";

/** Figma 6860:34066 — card width with side peek via centre alignment. */
const MOBILE_CARD_WIDTH = "w-[327px]";

/** Figma 6890:41517 — white ↔ purple crossfade timing. */
const CARD_COLOR_TRANSITION_MS = 0.6;
const CARD_COLOR_EASE = [0.4, 0, 0.2, 1] as const;
const CARD_SURFACE_TRANSITION =
  "transition-[min-height,margin,height,box-shadow] duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none";

const DESKTOP_SLOT_LAYOUT = {
  0: { top: 27, left: "0%", right: "auto", x: "0%", zIndex: 0 },
  1: { top: 0, left: "50%", right: "auto", x: "-50%", zIndex: 10 },
  2: { top: 27, left: "auto", right: "0%", x: "0%", zIndex: 0 },
} as const;

function getSlot(cardIndex: number, activeIndex: number): 0 | 1 | 2 {
  if (cardIndex === activeIndex) return 1;
  if (cardIndex === (activeIndex + 2) % 3) return 0;
  return 2;
}

function buildCards(
  left: ThreeKeyStatCard,
  center: ThreeKeyStatCard,
  right: ThreeKeyStatCard
): StatCardData[] {
  return [
    { ...left, visual: "revenue" },
    { ...center, visual: "customers" },
    { ...right, visual: "timeline" },
  ];
}

export function ThreeKeyStats({
  left,
  center,
  right,
  defaultActiveIndex = 1,
  className,
  "data-testid": dataTestId = "three-key-stats-section",
}: ThreeKeyStatsProps) {
  const cards = buildCards(left, center, right);
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const prefersReducedMotion = useReducedMotion();

  const handleSelect = useCallback((index: number) => {
    setActiveIndex(index as 0 | 1 | 2);
  }, []);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setActiveIndex(carouselApi.selectedScrollSnap() as 0 | 1 | 2);
    };

    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const scrollToIndex = useCallback(
    (index: number) => {
      setActiveIndex(index as 0 | 1 | 2);
      carouselApi?.scrollTo(index);
    },
    [carouselApi]
  );

  return (
    <section
      data-testid={dataTestId}
      className={cn(
        "bg-grey-50 px-6 py-8 md:px-8 lg:px-20 lg:py-20",
        className
      )}
    >
      {/* Mobile carousel — centred featured card (Figma 6860:34066). */}
      <Carousel
        id="three-key-stats-carousel"
        setApi={setCarouselApi}
        opts={{
          align: "center",
          startIndex: defaultActiveIndex,
          containScroll: "trimSnaps",
        }}
        className="relative -mx-6 h-[565px] max-w-none overflow-visible md:hidden"
      >
        <CarouselContent className="-ml-0 h-full items-start gap-0">
          {cards.map((card, index) => {
            const isActive = index === activeIndex;
            return (
              <CarouselItem
                key={card.visual}
                className="shrink-0 basis-[327px] pl-0"
              >
                <StatCardVisual
                  {...card}
                  isActive={isActive}
                  onActivate={() => scrollToIndex(index)}
                  dataStatCard={card.visual}
                  className={cn(
                    MOBILE_CARD_WIDTH,
                    CARD_SURFACE_TRANSITION,
                    isActive
                      ? cn("h-[501px]", FEATURED_SHADOW)
                      : "mt-[58px] h-[400px]"
                  )}
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {/* Tablet + desktop: overlapping 3-up with click-to-centre (Figma 8106:57264 / 6798:46464). */}
      <OverlappingStatsLayout
        cards={cards}
        activeIndex={activeIndex}
        onSelect={handleSelect}
        prefersReducedMotion={prefersReducedMotion}
        className="relative mx-auto hidden h-[709px] max-w-[1280px] md:block xl:h-[549px]"
      />
    </section>
  );
}

function OverlappingStatsLayout({
  cards,
  activeIndex,
  onSelect,
  prefersReducedMotion,
  className,
}: {
  cards: StatCardData[];
  activeIndex: number;
  onSelect: (index: number) => void;
  prefersReducedMotion: boolean | null;
  className?: string;
}) {
  return (
    <div className={className}>
      {cards.map((card, index) => {
        const slot = getSlot(index, activeIndex);
        const isActive = index === activeIndex;
        const layout = DESKTOP_SLOT_LAYOUT[slot];

        return (
          <motion.div
            key={card.visual}
            className={cn("absolute", OVERLAP_CARD_WIDTH)}
            initial={false}
            animate={{
              top: layout.top,
              left: layout.left,
              right: layout.right,
              x: layout.x,
              zIndex: layout.zIndex,
            }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { type: "spring", stiffness: 260, damping: 28 }
            }
          >
            <StatCardVisual
              {...card}
              isActive={isActive}
              onActivate={() => onSelect(index)}
              dataStatCard={card.visual}
              className={cn(
                "w-full",
                CARD_SURFACE_TRANSITION,
                isActive
                  ? cn(OVERLAP_FEATURED_HEIGHT, FEATURED_SHADOW)
                  : OVERLAP_SIDE_HEIGHT
              )}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

function StatCardVisual({
  title,
  description,
  visual,
  isActive,
  onActivate,
  className,
  dataStatCard,
}: StatCardData & {
  isActive: boolean;
  onActivate: () => void;
  className?: string;
  dataStatCard?: string;
}) {
  return (
    <article
      data-stat-card={dataStatCard}
      data-active={isActive ? "true" : "false"}
      className={cn(
        "border-grey-100 bg-grey-0 relative cursor-pointer overflow-hidden rounded-2xl border p-6 md:p-[69px]",
        className
      )}
    >
      <button
        type="button"
        aria-pressed={isActive}
        aria-label={`${title}. ${isActive ? "Selected" : "Select to focus"}`}
        onClick={onActivate}
        className="focus-visible:ring-pulse-700 absolute inset-0 z-20 rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      />

      {/* Timeline default — dot grid on white (Figma 6890:41517). */}
      {!isActive && visual === "timeline" ? (
        <DottedGridOverlay color="rgba(0,0,0,0.05)" size={16} className="z-0" />
      ) : null}

      {/* Purple treatment — card-specific artboard per Figma 6890:41517. */}
      <motion.div
        aria-hidden
        initial={false}
        animate={{ opacity: isActive ? 1 : 0 }}
        transition={{
          duration: CARD_COLOR_TRANSITION_MS,
          ease: CARD_COLOR_EASE,
        }}
        className="pointer-events-none absolute inset-0 z-[1] overflow-hidden"
      >
        <ActiveCardBackground visual={visual} />
      </motion.div>

      <CrossfadeStatText
        title={title}
        description={description}
        isActive={isActive}
      />

      {visual === "customers" && <CustomersVisual />}
      {visual === "revenue" && <RevenueVisual isActive={isActive} />}
      {visual === "timeline" && <TimelineVisual isActive={isActive} />}
    </article>
  );
}

function CrossfadeStatText({
  title,
  description,
  isActive,
}: {
  title: string;
  description: string;
  isActive: boolean;
}) {
  return (
    <div
      className={cn(
        "relative z-10 flex max-w-full flex-col md:max-w-[308px]",
        isActive ? "gap-3 md:gap-4" : "gap-3"
      )}
    >
      <div className="relative">
        <motion.h3
          initial={false}
          animate={{ opacity: isActive ? 0 : 1 }}
          transition={{
            duration: CARD_COLOR_TRANSITION_MS,
            ease: CARD_COLOR_EASE,
          }}
          className="text-grey-950 text-[24px] leading-[1.2] tracking-[-0.04em] text-balance md:text-[32px]"
        >
          {title}
        </motion.h3>
        <motion.h3
          aria-hidden={!isActive}
          initial={false}
          animate={{ opacity: isActive ? 1 : 0 }}
          transition={{
            duration: CARD_COLOR_TRANSITION_MS,
            ease: CARD_COLOR_EASE,
          }}
          className="absolute inset-0 text-[22px] leading-[1.2] tracking-[-0.04em] text-balance text-white md:text-[32px]"
        >
          {title}
        </motion.h3>
      </div>
      <div className="relative">
        <motion.p
          initial={false}
          animate={{ opacity: isActive ? 0 : 1 }}
          transition={{
            duration: CARD_COLOR_TRANSITION_MS,
            ease: CARD_COLOR_EASE,
          }}
          className="text-grey-600 text-[16px] leading-[1.5] tracking-[-0.02em] text-balance"
        >
          {description}
        </motion.p>
        <motion.p
          aria-hidden={!isActive}
          initial={false}
          animate={{ opacity: isActive ? 1 : 0 }}
          transition={{
            duration: CARD_COLOR_TRANSITION_MS,
            ease: CARD_COLOR_EASE,
          }}
          className="absolute inset-0 text-[15px] leading-[1.5] tracking-[-0.02em] text-balance text-white md:text-[16px]"
        >
          {description}
        </motion.p>
      </div>
    </div>
  );
}

function ActiveCardBackground({ visual }: { visual: CardVisual }) {
  if (visual === "revenue") {
    // Figma 6890:41512 — gradient offset toward revenue mockup.
    return (
      <PurpleGradientImage className="bottom-0 left-[calc(50%+457px)] -translate-x-1/2" />
    );
  }

  if (visual === "timeline") {
    // Figma 6890:41515 — gradient plus rotated vector overlays.
    return (
      <>
        <PurpleGradientImage className="-bottom-[9px] -left-px" />
        <div className="absolute top-1/2 left-[calc(50%+194px)] flex h-[607px] w-[852px] -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <div className="rotate-90">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets-next/hosted-capital/key-stats/timeline-vector-active-figma.svg"
              alt=""
              width={852}
              height={607}
              className="max-w-none"
              draggable={false}
            />
          </div>
        </div>
      </>
    );
  }

  // Customers — Figma 6890:41614 / 6890:41912.
  return <PurpleGradientImage className="-bottom-[9px] -left-px" />;
}

function PurpleGradientImage({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "absolute flex h-[772px] w-[1372px] items-center justify-center",
        className
      )}
    >
      <div className="-scale-y-100">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/assets-next/hosted-capital/key-stats/featured-gradient-figma.png"
          alt=""
          width={1372}
          height={772}
          className="max-w-none object-cover"
          draggable={false}
        />
      </div>
    </div>
  );
}

function CustomersVisual() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[2] overflow-hidden"
    >
      <div className="absolute bottom-0 left-[24px] origin-bottom-left scale-[0.716] md:top-[161px] md:bottom-auto md:left-[30px] md:origin-top-left md:scale-100">
        <div className="relative h-[388px] w-[397px]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets-next/hosted-capital/key-stats/featured-ellipse-stack-figma.svg"
            alt=""
            className="pointer-events-none absolute top-px left-px size-[822px] max-w-none mix-blend-soft-light"
            draggable={false}
          />

          <FeaturedProfileBubble
            avatarSrc="https://cdn.sanity.io/images/1aendizt/redesign/5bdf229e97f6c6148c78ad16642b06e33bb0d1c3-1080x1080.jpg"
            className="top-[65px] left-[306px]"
            size="lg"
          />
          <FeaturedProfileBubble
            avatarSrc="https://cdn.sanity.io/images/1aendizt/redesign/fa7c3d09389537d452df30a8be1863205ca7fc92-400x400.jpg"
            className="top-[99px] left-[78px]"
            size="md"
          />
          <FeaturedProfileBubble
            avatarSrc="https://cdn.sanity.io/images/1aendizt/redesign/4ace6a5d9b6baf88d25a8e844aaea7dcb71b8a13-1080x1080.jpg"
            className="top-[293px] left-[172px]"
            size="md"
          />
          <FeaturedProfileBubble
            avatarSrc="https://cdn.sanity.io/images/1aendizt/redesign/7758d9ccfc28769b44d198b3da200fdc1b1b1b06-1080x1080.jpg"
            badgeSrc="/assets-next/hosted-capital/key-stats/featured-check-badge-figma.svg"
            badgeClassName="top-[52px] left-[52px]"
            className="top-[217px] left-0"
            size="lg"
          />
          <FeaturedProfileBubble
            avatarSrc="https://cdn.sanity.io/images/1aendizt/redesign/9958c604d9341759b7c0d01e69987936cc37820c-1080x1080.jpg"
            className="top-[167px] left-[202px]"
            size="lg"
          >
            <div className="absolute top-[52px] left-[52px] size-[25px] rounded-full bg-[#cc61cc] shadow-[inset_0.86px_1.721px_10px_rgba(255,255,255,0.25)]">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/assets-next/hosted-capital/key-stats/featured-heart-badge-figma.svg"
                alt=""
                width={14}
                height={14}
                className="absolute top-1/2 left-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2"
                draggable={false}
              />
            </div>
          </FeaturedProfileBubble>

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets-next/hosted-capital/key-stats/featured-dot-large-figma.svg"
            alt=""
            width={30}
            height={30}
            className="pointer-events-none absolute top-[72px] left-[208px] size-[30px]"
            draggable={false}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets-next/hosted-capital/key-stats/featured-dot-small-figma.svg"
            alt=""
            width={18}
            height={18}
            className="pointer-events-none absolute top-[246px] left-[143px] size-[18px]"
            draggable={false}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets-next/hosted-capital/key-stats/featured-plus-badge-figma.svg"
            alt=""
            width={25}
            height={25}
            className="pointer-events-none absolute top-[117px] left-[357px] size-[25px]"
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
}

function FeaturedProfileBubble({
  avatarSrc,
  badgeSrc,
  badgeClassName,
  children,
  className,
  size,
}: {
  avatarSrc: string;
  badgeSrc?: string;
  badgeClassName?: string;
  children?: ReactNode;
  className?: string;
  size: "lg" | "md";
}) {
  const frameSize = size === "lg" ? "size-[77px]" : "size-[62px]";
  const innerSize = size === "lg" ? "size-[59px]" : "size-[46px]";

  return (
    <div className={cn("absolute", frameSize, className)}>
      <div className="relative flex size-full items-center justify-center overflow-hidden rounded-full border-[1.5px] border-white bg-white/50 backdrop-blur-[2px]">
        <div className={cn("relative overflow-hidden rounded-full", innerSize)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={avatarSrc}
            alt=""
            className="size-full object-cover"
            draggable={false}
          />
        </div>
      </div>
      {badgeSrc ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={badgeSrc}
          alt=""
          width={25}
          height={25}
          className={cn("absolute size-[25px]", badgeClassName)}
          draggable={false}
        />
      ) : null}
      {children}
    </div>
  );
}

function RevenueVisual({ isActive }: { isActive: boolean }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[2] overflow-hidden"
    >
      <div className="absolute bottom-0 left-[24px] origin-bottom-left scale-[0.716] md:top-0 md:bottom-auto md:left-0 md:origin-top-left md:scale-100">
        <div className="relative h-[497px] w-[457px]">
          <div
            className={cn(
              "absolute left-[69px] h-[284px] w-[425px] max-w-none overflow-visible transition-[top] duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
              isActive
                ? "top-[277px] md:top-[277px]"
                : "top-[330px] md:top-[380px]"
            )}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets-next/hosted-capital/key-stats/revenue-screen.png"
              alt=""
              className="size-full object-contain object-left-top"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineVisual({ isActive }: { isActive: boolean }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[2] overflow-hidden"
    >
      {/* Inactive lilac vector — fades out when purple active state enters. */}
      <motion.div
        initial={false}
        animate={{ opacity: isActive ? 0 : 1 }}
        transition={{
          duration: CARD_COLOR_TRANSITION_MS,
          ease: CARD_COLOR_EASE,
        }}
        className="absolute inset-0 overflow-hidden"
      >
        <div className="absolute top-1/2 left-[calc(50%+194px)] flex h-[607px] w-[852px] -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <div className="rotate-90">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets-next/hosted-capital/key-stats/timeline-vector-default-figma.svg"
              alt=""
              width={852}
              height={607}
              className="max-w-none"
              draggable={false}
            />
          </div>
        </div>
      </motion.div>

      <div className="absolute top-[96px] left-[24px] origin-top-left scale-[0.716] md:top-0 md:left-0 md:scale-100">
        <div className="relative h-[497px] w-[457px]">
          <div className="absolute top-[262px] left-[69px] h-[209px] w-[425px] max-w-none">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets-next/hosted-capital/key-stats/timeline-screen.png"
              alt=""
              className="size-full object-contain object-left-top"
              draggable={false}
            />
          </div>
          <div
            className={cn(
              "border-grey-100 bg-grey-0 absolute right-[52px] rounded-2xl border px-5 py-3 shadow-[6px_10px_28px_rgba(0,0,0,0.08)] transition-[top] duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
              isActive ? "top-[244px]" : "top-[196px]"
            )}
          >
            <p className="text-grey-500 text-[13px] leading-[1.3] tracking-[-0.02em]">
              Available in:
            </p>
            <p className="text-grey-950 text-[28px] leading-none font-medium tracking-[-0.04em]">
              36{" "}
              <span className="text-grey-500 text-[15px] font-normal">hr</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
